import { useCallback, useEffect, useState } from 'react';
import { onboardingAPI, intelligenceAPI } from '../../lib/api';
import type { FilledDataset, SupplierQualification } from '../../lib/api';
import { mergeRows, type SupplierQuestionnaireRow } from './rows';

interface SupplierQuestionnairesState {
  rows: SupplierQuestionnaireRow[];
  loading: boolean;
  /** Set when BOTH sources failed — nothing to show. */
  error: string | null;
  /** Set when one source failed — the list is shown but flagged as partial. */
  partial: string | null;
  reload: () => void;
}

const PAGE_SIZE = 200;

/**
 * CR-5865: loads the supplier's questionnaires from both assignment systems and
 * merges them into one list.
 *
 * The two requests are independent, so `Promise.allSettled` keeps one failing
 * source from blanking the other — the supplier still sees what loaded, with a
 * note that part of the list is missing.
 *
 * @returns merged rows plus loading / error / partial flags and a reload.
 */
export function useSupplierQuestionnaires(): SupplierQuestionnairesState {
  const [rows, setRows] = useState<SupplierQuestionnaireRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [partial, setPartial] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPartial(null);

    Promise.allSettled([
      onboardingAPI.getFilledDatasets({ page_size: PAGE_SIZE }),
      intelligenceAPI.getQualifications({ page_size: PAGE_SIZE }),
    ]).then(([datasetsResult, qualificationsResult]) => {
      if (cancelled) return;

      const datasets: FilledDataset[] = datasetsResult.status === 'fulfilled'
        ? datasetsResult.value.data.results ?? []
        : [];
      const qualifications: SupplierQualification[] = qualificationsResult.status === 'fulfilled'
        ? qualificationsResult.value.data.results ?? []
        : [];

      const failed: string[] = [];
      if (datasetsResult.status === 'rejected') {
        console.error('Failed to load onboarding questionnaires:', datasetsResult.reason);
        failed.push('onboarding questionnaires');
      }
      if (qualificationsResult.status === 'rejected') {
        console.error('Failed to load qualification questionnaires:', qualificationsResult.reason);
        failed.push('assessment questionnaires');
      }

      if (failed.length === 2) {
        setError('Failed to load your questionnaires. Please try again.');
        setRows([]);
      } else {
        setRows(mergeRows(datasets, qualifications));
        setPartial(failed.length ? `Some ${failed[0]} could not be loaded.` : null);
      }
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [tick]);

  return { rows, loading, error, partial, reload };
}
